import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import {
  FormControl, InputLabel, MenuItem, Select, Box, Paper, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { fetchWithAuth } from '../services/api';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function Performance() {
  const [comparisonData, setComparisonData] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState('');
  const [startMonth, setStartMonth] = useState(''); // '' = All History
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch comparison data
    setLoading(true);
    fetchWithAuth('/api/data/portfolio/portfolio-comparison')
      .then(response => response.json())
      .then(data => {
        setComparisonData(data);
        // Get unique owners from the data
        const uniqueOwners = [...new Set(data.map(item => item.group))];
        setOwners(uniqueOwners);
        if (uniqueOwners.length > 0) {
          setSelectedOwner(uniqueOwners[0]);
        }
      })
      .catch(error => console.error('Error fetching comparison data:', error))
      .finally(() => setLoading(false));
  }, []);

  // Filter data for selected owner
  const ownerData = comparisonData.filter(item => item.group === selectedOwner);
  const availableMonths = ownerData.map(item => item.report_month);
  // report_month is an ISO date, so string comparison orders it correctly.
  const filteredData = startMonth
    ? ownerData.filter(item => item.report_month >= startMonth)
    : ownerData;

  const formatMonth = (isoDate) => new Date(`${isoDate}T00:00:00`)
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  // Every owner in the latest month, whatever the dropdown shows; Total last.
  const latestMonth = comparisonData.reduce(
    (latest, item) => (item.report_month > latest ? item.report_month : latest), ''
  );
  const summaryRows = comparisonData
    .filter(item => item.report_month === latestMonth)
    .sort((a, b) => (a.group === 'All') - (b.group === 'All') || String(a.group).localeCompare(String(b.group)));
  const formatDollars = (value) => (value == null ? '—' : `$${Math.round(value).toLocaleString()}`);
  const formatPercent = (value) => (value == null ? '—' : `${value.toFixed(1)}%`);
  const contractShareOfPha = (item) => (item.pha_noi ? item.contract_noi / item.pha_noi * 100 : null);
  const summaryColumns = [
    ['Trailing Net Cash Flow', item => formatDollars(item.trailing_net_cash_flow)],
    ['Contract % of PHA', item => formatPercent(contractShareOfPha(item))],
    ['Occupancy Rate', item => formatPercent(item.occupancy_rate)],
    ['Maintenance per Unit', item => formatDollars(item.maintenance_per_unit)],
    ['Turnover/Capex per Unit', item => formatDollars(item.turnover_capex_per_unit)],
    ['Water per Unit', item => formatDollars(item.water_per_unit)],
  ];

  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12 },
          boxWidth: 10
        }
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false
      }
    },
    layout: {
      padding: {
        left: 10,
        right: 20,
        top: 0,
        bottom: 10
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${value.toLocaleString()}`,
          font: { size: 12 },
          maxTicksLimit: 8
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: { size: 12 },
          maxTicksLimit: 12
        },
        grid: {
          display: false
        }
      }
    }
  };

  const noiChartOptions = {
    ...commonChartOptions,
    plugins: {
      ...commonChartOptions.plugins,
      title: {
        display: true,
        text: 'Net Cash Flow Comparison',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
    }
  };

  const rentChartOptions = {
    ...commonChartOptions,
    plugins: {
      ...commonChartOptions.plugins,
      title: {
        display: true,
        text: 'Rent Comparison',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
    }
  };

  const occupancyChartOptions = {
    ...commonChartOptions,
    plugins: {
      ...commonChartOptions.plugins,
      title: {
        display: true,
        text: 'Occupancy Rate',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
    },
    scales: {
      ...commonChartOptions.scales,
      y: {
        beginAtZero: true,
        suggestedMax: 100,
        ticks: {
          callback: (value) => `${value}%`,
          font: { size: 12 },
          maxTicksLimit: 8
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    }
  };

  const maintenanceChartOptions = {
    ...commonChartOptions,
    plugins: {
      ...commonChartOptions.plugins,
      title: {
        display: true,
        text: 'Annual Maintenance, Turnover & Capex per Unit',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
    }
  };

  const waterChartOptions = {
    ...commonChartOptions,
    plugins: {
      ...commonChartOptions.plugins,
      title: {
        display: true,
        text: 'Monthly Water per Unit',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
    }
  };

  const getFormattedLabels = (data) => {
    return data.map(item => formatMonth(item.report_month));
  };

  const noiChartData = {
    labels: getFormattedLabels(filteredData),
    datasets: [
      {
        label: 'Trailing Net Cash Flow',
        data: filteredData.map(item => item.trailing_net_cash_flow),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'Contract Net Cash Flow',
        data: filteredData.map(item => item.contract_noi),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.5)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'PHA Net Cash Flow',
        data: filteredData.map(item => item.pha_noi),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const rentChartData = {
    labels: getFormattedLabels(filteredData),
    datasets: [
      {
        label: 'Revenue',
        data: filteredData.map(item => item.monthly_revenue),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'Rent Roll',
        data: filteredData.map(item => item.rent_roll),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.5)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'PHA Rent',
        data: filteredData.map(item => item.pha_rent),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const occupancyChartData = {
    labels: getFormattedLabels(filteredData),
    datasets: [
      {
        label: 'Occupancy Rate',
        data: filteredData.map(item => item.occupancy_rate),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const maintenanceChartData = {
    labels: getFormattedLabels(filteredData),
    datasets: [
      {
        label: 'Maintenance per Unit',
        data: filteredData.map(item => item.maintenance_per_unit),
        borderColor: 'rgb(255, 206, 86)',
        backgroundColor: 'rgba(255, 206, 86, 0.5)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'Turnover/Capex per Unit',
        data: filteredData.map(item => item.turnover_capex_per_unit),
        borderColor: 'rgb(255, 159, 64)',
        backgroundColor: 'rgba(255, 159, 64, 0.5)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'Total Maintenance, Turnover, and Capex per Unit',
        data: filteredData.map(item => item.total_spend_per_unit),
        borderColor: 'rgb(75, 85, 99)',
        backgroundColor: 'rgba(75, 85, 99, 0.5)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const waterChartData = {
    labels: getFormattedLabels(filteredData),
    datasets: [
      {
        label: 'Water per Unit',
        data: filteredData.map(item => item.water_per_unit),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  return (
    <Box sx={{ 
      width: '100%',
      p: 3 
    }}>
      <Paper elevation={2} sx={{ p: 3, width: '100%' }}>
        {summaryRows.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              {formatMonth(latestMonth)}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Owner</TableCell>
                    {summaryColumns.map(([label]) => (
                      <TableCell key={label} align="right">{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summaryRows.map((item) => (
                    <TableRow key={item.group}>
                      <TableCell sx={item.group === 'All' ? { fontWeight: 'bold' } : undefined}>
                        {item.group === 'All' ? 'Total' : item.group}
                      </TableCell>
                      {summaryColumns.map(([label, value]) => (
                        <TableCell
                          key={label}
                          align="right"
                          sx={item.group === 'All' ? { fontWeight: 'bold' } : undefined}
                        >
                          {value(item)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3
        }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="owner-select-label">Owner</InputLabel>
            <Select
              labelId="owner-select-label"
              value={selectedOwner}
              label="Owner"
              onChange={(e) => {
                const owner = e.target.value;
                setSelectedOwner(owner);
                // An owner acquired later may not have the chosen month.
                const months = comparisonData
                  .filter(item => item.group === owner)
                  .map(item => item.report_month);
                if (startMonth && !months.includes(startMonth)) setStartMonth('');
              }}
              disabled={loading}
            >
              {owners.map((owner) => (
                <MenuItem key={owner} value={owner}>
                  {owner}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="start-month-select-label" shrink>Starting Month</InputLabel>
            <Select
              labelId="start-month-select-label"
              value={startMonth}
              label="Starting Month"
              onChange={(e) => setStartMonth(e.target.value)}
              disabled={loading}
              displayEmpty
              notched
              renderValue={(value) => (value ? formatMonth(value) : 'All History')}
            >
              <MenuItem value="">All History</MenuItem>
              {availableMonths.map((month) => (
                <MenuItem key={month} value={month}>
                  {formatMonth(month)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '400px' 
          }}>
            Loading...
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 4,
            width: '100%'
          }}>
            <Box sx={{ height: '400px', width: '100%', position: 'relative' }}>
              <Line data={noiChartData} options={noiChartOptions} />
            </Box>
            <Box sx={{ height: '400px', width: '100%', position: 'relative' }}>
              <Line data={rentChartData} options={rentChartOptions} />
            </Box>
            <Box sx={{ height: '400px', width: '100%', position: 'relative' }}>
              <Line data={occupancyChartData} options={occupancyChartOptions} />
            </Box>
            <Box sx={{ height: '400px', width: '100%', position: 'relative' }}>
              <Line data={maintenanceChartData} options={maintenanceChartOptions} />
            </Box>
            <Box sx={{ height: '400px', width: '100%', position: 'relative' }}>
              <Line data={waterChartData} options={waterChartOptions} />
            </Box>              
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default Performance;
